import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  LinkButton,
  OptionKey,
  PlusThinIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { EditableBadge } from '@/modules/UseMyMoney/components';
import { CreatedPatientDto, CreatePatientDto } from '@/modules/UseMyMoney/queries';
import { useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { usePatientOptions } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks';

import FieldSetupLabel from '../FieldSetupLabel/FieldSetupLabel';
import FieldSetupOptions from '../FieldSetupOptions/FieldSetupOptions';

import AddFamilyMemberModal from './AddFamilyMemberModal';

interface Props {
  createPatient: (createPatientDto: CreatePatientDto) => Promise<CreatedPatientDto>;
}

const ServiceForFieldSetup: React.FC<Props> = ({  createPatient }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { values, handleSetFormValues } = useMyMoneyFormStore();
  const [isAddPatientModalVisible, setIsAddPatientModalVisible] = useState(false);
  const [providerFullName, setProviderFullName] = useState('');
  const {
    suggestedPatientOptions,
    patientOptions,
    activePatientOption,
  } = usePatientOptions({
    patientId: values?.patientId,
    setIsAddPatientModalVisible,
    setProviderFullName,
  });
  const [isOptionsExpanded, setIsOptionsExpanded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!values?.patientId);
  const handleChangePatient = useCallback((patientId: string | OptionKey) => {
    handleSetFormValues({ patientId: patientId as string });
    setIsEditMode(false);
  }, [handleSetFormValues]);

  const onAddPatientDetails = useCallback((createPatientPayload: Record<string, string>) => {
    setProviderFullName(createPatientPayload?.['full_name']);
    setIsAddPatientModalVisible(true);
  }, []);

  const visiblePatientOptions = useMemo(() => isOptionsExpanded ? patientOptions : patientOptions?.slice(0, 3),
    [isOptionsExpanded, patientOptions]);

  const handleOpenAddPatientModal = useCallback(() => {
    setIsAddPatientModalVisible(true);
  }, []);

  const handleExpandOptions = useCallback(() => {
    setIsOptionsExpanded(true);
  }, []);

  return (
    <>
      <Box data-testid="patient-setup">
        <FieldSetupLabel
          testId="patient"
          hasValue={!!values?.patientId}
          title={t('Who was this expense for?')}
          isMobile={isMobile}
        />

        {suggestedPatientOptions?.length ? (
          <Box 
            data-testid="suggested-patients-options"
            margin={{ top: 'spacing24' }}
          >
            <Text weight={700}>
              {suggestedPatientOptions?.length > 1 
                ? t('Family members found on your receipt')
                : t('Family member found on your receipt')}
            </Text>
            <FieldSetupOptions
              testId="suggested-patient"
              margin={{ top: 'spacing8' }}
              value={values?.patientId}
              onChangeValue={handleChangePatient}
              onCreateValue={onAddPatientDetails}
              options={suggestedPatientOptions}
              style={isMobile ? {
                width: '100%',
                flexWrap: 'wrap',
              } : {
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
              }}
            />
          </Box>
        ) : null}

        {/* Custom patient setup */}
        <Box margin={{ top: 'spacing24' }} align="start">
          <Text weight={700}>
            {activePatientOption?.key 
              ? t('Selected dependent')
              : t('Not right? Select or add a new one')}
          </Text>

          <EditableBadge
            margin={{ top: 'spacing8' }}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            title={activePatientOption?.title}
            value={activePatientOption?.key as string}
            isSelected={!!activePatientOption?.key}
          >
            <FieldSetupOptions
              testId="patient"
              margin={{ top: 'spacing8' }}
              onChangeValue={handleChangePatient}
              options={visiblePatientOptions}
              style={isMobile ? {
                width: '100%',
                flexWrap: 'wrap',
              } : {
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
              }}
            />

            <Box
              margin={{ top: 'spacing12' }}
              direction="row"
              justify="between"
              align="center"
            >
              {patientOptions?.length > 3 && !isOptionsExpanded ? (
                <LinkButton
                  testId="patients-lins_show-more"
                  onClick={handleExpandOptions}
                >
                  {t('Show More')} ({patientOptions?.length - 3})
                </LinkButton>
              ) : null}

              <AppButton
                testId="open-add-patient-modal"
                onClick={handleOpenAddPatientModal}
                buttonType="secondary"
                onlyIcon
              >
                <PlusThinIcon size="24px" />
              </AppButton>
            </Box>
          </EditableBadge>
        </Box>
      </Box>

      {isAddPatientModalVisible && (
        <AddFamilyMemberModal
          onSetVisible={setIsAddPatientModalVisible}
          createPatient={createPatient}
          handleChangePatient={handleChangePatient}
          providerFullName={providerFullName}
          setProviderFullName={setProviderFullName}
        />
      )}
    </>
  );
};

export default ServiceForFieldSetup;
